import { useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { CheckCircle2, XCircle, Loader2 } from "lucide-react"

import { authService } from "@/features/auth/services/authService"
import { APP_NAME } from "@/lib/appConfig"
import { Button } from "@/components/ui/button"

type State = "loading" | "success" | "error"

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token")
  const [state, setState] = useState<State>("loading")
  const [errorMessage, setErrorMessage] = useState<string>("")

  useEffect(() => {
    const prev = document.title
    document.title = `Verify Email | ${APP_NAME}`
    return () => { document.title = prev }
  }, [])

  useEffect(() => {
    if (!token) {
      setState("error")
      setErrorMessage("Verification token is missing.")
      return
    }

    authService
      .verifyEmail(token)
      .then(() => setState("success"))
      .catch((err: unknown) => {
        setState("error")
        setErrorMessage(
          err instanceof Error
            ? err.message
            : "Verification failed. The link may be expired or invalid."
        )
      })
  }, [token])

  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-background">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Verifying your email...</p>
        </div>
      </div>
    )
  }

  if (state === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-background">
        <div className="w-full max-w-sm text-center">
          <div className="w-20 h-20 rounded-3xl bg-green-100 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="h-9 w-9 text-green-600" />
          </div>
          <h2 className="text-xl font-bold mb-2">Email verified!</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Your email address has been verified successfully. You can now access
            all features.
          </p>
          <Link to="/login">
            <Button className="h-11 px-6 rounded-xl">Go to login</Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-background">
      <div className="w-full max-w-sm text-center">
        <div className="w-20 h-20 rounded-3xl bg-red-100 flex items-center justify-center mx-auto mb-5">
          <XCircle className="h-9 w-9 text-red-600" />
        </div>
        <h2 className="text-xl font-bold mb-2">Verification failed</h2>
        <p className="text-sm text-muted-foreground mb-6">{errorMessage}</p>
        <div className="flex flex-col gap-3 items-center">
          <Link to="/login">
            <Button className="h-11 px-6 rounded-xl">Go to login</Button>
          </Link>
        </div>
      </div>
    </div>
  )
}
